import { useEffect } from 'react';

// ─────────────────────────────────────────────────────────────
// useVisitTracking — analytics anonymisées, une fois par session
// navigateur (pas à chaque changement de page).
//
// CORRECTIF v2 : le hook lit maintenant directement
// `localStorage.getItem('kotos_session_type')` — déjà maintenu par
// AppContext.js (login/logout/enterVisitorMode/exitVisitorMode) —
// au lieu de recevoir un simple booléen `isInterne`. Ça permet de
// distinguer les 3 cas attendus par AdminPanel.jsx (onglet Visiteurs) :
//   - 'interne'         → ouvrier/manager/admin connecté normalement
//   - 'visiteur_admin'  → admin en train de prévisualiser "Vue Client"
//   - absent/autre      → vrai visiteur externe (compté "Visiteurs Clients")
// Avant ce correctif, le cas "Vue Client" de l'admin comptait par erreur
// comme un vrai client (currentUser devient null pendant cette
// prévisualisation), faussant les deux compteurs.
//
// Ce qui est enregistré : ville/pays/région APPROXIMATIFS déduits de
// l'adresse IP (résolution niveau ville, comme n'importe quel outil
// d'analytics grand public — Google Analytics, Plausible, etc.),
// le type d'appareil/navigateur, la page d'entrée et le référent.
//
// Ce qui n'est JAMAIS enregistré, et ne peut techniquement pas
// l'être sans que la personne ne s'identifie elle-même (formulaire,
// compte...) : son nom, son identité réelle, ou son adresse postale
// précise. Une adresse IP ne donne qu'une zone approximative
// (souvent la ville du fournisseur d'accès, parfois à plusieurs
// dizaines de km du lieu réel), jamais une adresse physique exacte
// ni un nom — et tenter de relier une IP à une identité réelle sans
// consentement relèverait de la collecte de données personnelles non
// autorisée (RGPD et cadres équivalents), pas d'un simple réglage
// technique.
// ─────────────────────────────────────────────────────────────
export function useVisitTracking(trackVisite) {
  useEffect(() => {
    if (sessionStorage.getItem('kotos_visite_loggee')) return; // 1 fois par session
    sessionStorage.setItem('kotos_visite_loggee', '1');

    async function run() {
      let geo = {};
      try {
        const resp = await fetch('https://ipapi.co/json/');
        const g = await resp.json();
        geo = {
          ville:   g.city    || 'Inconnue',
          region:  g.region  || '',
          pays:    g.country_name || 'Inconnu',
          fai:     g.org     || '',
        };
      } catch (e) {
        geo = { ville: 'Inconnue', region: '', pays: 'Inconnu', fai: '' };
      }

      // ← Lu directement depuis localStorage, source de vérité déjà
      // maintenue par AppContext.js pour les 3 états possibles.
      const sessionType = localStorage.getItem('kotos_session_type'); // 'interne' | 'visiteur_admin' | null
      const isEquipe = sessionType === 'interne' || sessionType === 'visiteur_admin';

      trackVisite({
        ...geo,
        type: sessionType === 'interne' ? 'interne'
            : sessionType === 'visiteur_admin' ? 'visiteur_admin'
            : 'visiteur',
        membre: isEquipe ? (localStorage.getItem('kotos_session_name') || '') : '',
        role:   isEquipe ? (localStorage.getItem('kotos_session_role') || '') : '',
        appareil: /Mobi|Android/i.test(navigator.userAgent) ? 'Mobile' : 'Ordinateur',
        navigateur: (() => {
          const ua = navigator.userAgent;
          if (ua.includes('Chrome')) return 'Chrome';
          if (ua.includes('Firefox')) return 'Firefox';
          if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
          if (ua.includes('Edg')) return 'Edge';
          return 'Autre';
        })(),
        referent: document.referrer || 'Direct',
        pageEntree: window.location.pathname + window.location.hash,
      });
    }

    run();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}